import React, { useState, useEffect, useMemo } from 'react';
import { collection, query, orderBy, onSnapshot, addDoc, serverTimestamp, getFirestore, collectionGroup, where, getDocs } from 'firebase/firestore';
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import app from '../../firebase/firebaseConfig';
import TransactionTable from './TransactionTable';
import AddTransactionModal from './AddTransactionModal';
import { useAuth } from '../../context/AuthContext';

const FinanceDashboard = () => {
    const db = getFirestore(app);
    const { currentUser } = useAuth();
    const farmId = currentUser.farmId;

    // State for manual transactions
    const [manualTransactions, setManualTransactions] = useState([]);
    
    // --- NEW: State for automatic transactions ---
    const [milkIncome, setMilkIncome] = useState([]);
    const [feedExpenses, setFeedExpenses] = useState([]);
    const [healthExpenses, setHealthExpenses] = useState([]);

    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, setModalOpen] = useState(false);
    
    // --- NEW: State for filtering ---
    const [filterType, setFilterType] = useState('All');

    // Fetch manual transactions
    useEffect(() => {
        if (!farmId) return;
        const path = `/artifacts/${app.options.appId}/farms/${farmId}/transactions`;
        const q = query(collection(db, path), orderBy('date', 'desc'));
        const unsubscribe = onSnapshot(q, (snap) => {
            setManualTransactions(snap.docs.map(d => ({ ...d.data(), id: d.id, source: 'Manual' })));
            setIsLoading(false);
        });
        return () => unsubscribe();
    }, [farmId]);

    // --- NEW: Fetch data for automatic transactions ---
    useEffect(() => {
        if (!farmId) return;

        // --- FIX: Using a more robust method to fetch milk income ---
        const fetchMilkIncome = async () => {
            const livestockPath = `artifacts/${app.options.appId}/farms/${farmId}/livestock`;
            const livestockQuery = query(collection(db, livestockPath));
            const livestockSnapshot = await getDocs(livestockQuery);
            const animals = livestockSnapshot.docs.map(doc => doc.id);

            let allMilkRecords = [];
            const milkPromises = animals.map(async (animalId) => {
                const milkPath = `${livestockPath}/${animalId}/milk_records`;
                const milkSnapshot = await getDocs(collection(db, milkPath));
                return milkSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            });

            const results = await Promise.all(milkPromises);
            allMilkRecords = results.flat();
            
            const incomeFromMilk = allMilkRecords.map(record => {
                const dailyYield = (record.morningYield || 0) + (record.eveningYield || 0);
                const income = dailyYield * (record.pricePerLiter || 0);
                if (income <= 0) return null;
                return {
                    id: `milk-${record.id}`,
                    date: record.date,
                    type: 'income',
                    description: `Milk Sale - ${dailyYield.toFixed(1)}L`,
                    amount: income,
                    source: 'Automatic'
                };
            }).filter(Boolean);
            setMilkIncome(incomeFromMilk);
        };

        fetchMilkIncome();


        // 2. Fetch feed purchases (expense)
        const feedPurchasesRef = collection(db, `/artifacts/${app.options.appId}/farms/${farmId}/feed_purchases`);
        const feedQuery = query(feedPurchasesRef, orderBy('date', 'desc'));
        const unsubFeed = onSnapshot(feedQuery, (snap) => {
            const expensesFromFeed = snap.docs.map(d => {
                const purchase = d.data();
                return {
                    id: `feed-${d.id}`,
                    date: purchase.date,
                    type: 'expense',
                    category: 'Feed',
                    description: `Purchased ${purchase.quantity}kg of ${purchase.feedItemName}`,
                    amount: purchase.totalCost,
                    source: 'Automatic'
                };
            });
            setFeedExpenses(expensesFromFeed);
        });

        // 3. Fetch health costs (expense)
        const healthRecordsRef = collectionGroup(db, 'health_records');
        const healthQuery = query(healthRecordsRef, where('farmId', '==', farmId), where('cost', '>', 0));
        const unsubHealth = onSnapshot(healthQuery, (snap) => {
            const expensesFromHealth = snap.docs.map(d => {
                const record = d.data();
                return {
                    id: `health-${d.id}`,
                    date: record.date,
                    type: 'expense',
                    category: 'Medical',
                    description: `${record.eventType}: ${record.notes}`,
                    amount: record.cost,
                    source: 'Automatic'
                };
            });
            setHealthExpenses(expensesFromHealth);
        });

        return () => { unsubFeed(); unsubHealth(); };
    }, [farmId]);

    // --- NEW: Combine and filter all transactions ---
    const allTransactions = useMemo(() => {
        const combined = [...manualTransactions, ...milkIncome, ...feedExpenses, ...healthExpenses];
        combined.sort((a, b) => new Date(b.date) - new Date(a.date));
        if (filterType === 'All') {
            return combined;
        }
        return combined.filter(t => t.type === filterType.toLowerCase());
    }, [manualTransactions, milkIncome, feedExpenses, healthExpenses, filterType]);

    const handleAddTransaction = async (data) => {
        if (!farmId) return;
        const path = `/artifacts/${app.options.appId}/farms/${farmId}/transactions`;
        await addDoc(collection(db, path), {
            ...data,
            amount: Number(data.amount),
            farmId,
            createdAt: serverTimestamp()
        });
        setModalOpen(false);
    };

    const summary = allTransactions.reduce((acc, t) => {
        if (t.type === 'income') acc.income += t.amount;
        else acc.expense += t.amount;
        return acc;
    }, { income: 0, expense: 0 });

    const netProfit = summary.income - summary.expense;

    // --- NEW: Data for Pie Chart ---
    const pieChartData = [
        { name: 'Income', value: parseFloat(summary.income.toFixed(2)) },
        { name: 'Expense', value: parseFloat(summary.expense.toFixed(2)) },
    ];
    const PIE_COLORS = ['#22c55e', '#ef4444'];

    return (
        <div>
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-800">Financial Overview</h2>
                <button onClick={() => setModalOpen(true)} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700">+ Add Manual Transaction</button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="bg-white p-6 rounded-lg shadow"><p className="text-sm font-medium text-gray-500">Total Income</p><p className="mt-1 text-3xl font-semibold text-green-600">₹{summary.income.toFixed(2)}</p></div>
                <div className="bg-white p-6 rounded-lg shadow"><p className="text-sm font-medium text-gray-500">Total Expenses</p><p className="mt-1 text-3xl font-semibold text-red-600">₹{summary.expense.toFixed(2)}</p></div>
                <div className={`bg-white p-6 rounded-lg shadow ${netProfit >= 0 ? 'text-blue-600' : 'text-red-600'}`}><p className="text-sm font-medium text-gray-500">Net Profit</p><p className="mt-1 text-3xl font-semibold">₹{netProfit.toFixed(2)}</p></div>
            </div>

            {/* --- NEW: Pie Chart and Filter Section --- */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
                <div className="lg:col-span-1 bg-white p-4 rounded-lg shadow">
                    <h3 className="text-lg font-semibold mb-4 text-gray-800">Income vs. Expense</h3>
                    <ResponsiveContainer width="100%" height={250}>
                        <PieChart>
                            <Pie data={pieChartData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} fill="#8884d8">
                                {pieChartData.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                                ))}
                            </Pie>
                            <Tooltip formatter={(value) => `₹${value.toFixed(2)}`} />
                            <Legend />
                        </PieChart>
                    </ResponsiveContainer>
                </div>
                <div className="lg:col-span-2 p-4 bg-white rounded-lg shadow">
                    <div className="mb-4">
                        <label htmlFor="filter-type" className="text-sm font-medium text-gray-700">Filter Transactions:</label>
                        <select id="filter-type" value={filterType} onChange={(e) => setFilterType(e.target.value)} className="mt-1 block w-full form-select rounded-md shadow-sm">
                            <option value="All">All Transactions</option>
                            <option value="Income">Income Only</option>
                            <option value="Expense">Expenses Only</option>
                        </select>
                    </div>
                    <TransactionTable transactions={allTransactions} isLoading={isLoading} />
                </div>
            </div>

            <AddTransactionModal isOpen={isModalOpen} onClose={() => setModalOpen(false)} onSubmit={handleAddTransaction} />
        </div>
    );
};

export default FinanceDashboard;
